<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import { debounce } from '@chatwoot/utils';
import { dynamicCacheKey } from 'dashboard/store/modules/wgptAutomationRegistry';
import IconNext from 'next/icon/Icon.vue';
import ArrayField from './ArrayField.vue';
import AttachmentField from './AttachmentField.vue';
import BooleanField from './BooleanField.vue';
import CheckboxField from './CheckboxField.vue';
import DateField from './DateField.vue';
import EmailField from './EmailField.vue';
import validationMixins from './helpers/validationMixins';
import MultiSelectField from './MultiSelectionField.vue';
import NumberField from './NumberField.vue';
import RichtextField from './RichtextField.vue';
import SelectField from './SelectField.vue';
import TextField from './TextField.vue';
import UrlField from './UrlField.vue';
import VariableField from './VariableField.vue';

export default {
  name: 'DynamicField',
  components: {
    TextField,
    EmailField,
    UrlField,
    NumberField,
    DateField,
    SelectField,
    MultiSelectField,
    ArrayField,
    VariableField,
    RichtextField,
    AttachmentField,
    CheckboxField,
    BooleanField,
    IconNext,
  },
  mixins: [validationMixins],
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    field: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    parentSteps: {
      type: Array,
      default: () => [],
    },
    variables: {
      type: Array,
      default: () => [],
    },
    showVariablesSelector: {
      type: Boolean,
      default: true,
    },
    params: {
      type: Object,
      default: () => {},
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      dispatchWrappers: debounce(() => this.dispatchWrappersCallback(), 100),
      optionsCacheKey: null,
      fixedControlsCacheKey: null,
    };
  },
  computed: {
    isFieldDynamic() {
      return !!this.field.model;
    },
    isFieldModelWrapper() {
      return this.field.wrapper && this.isFieldDynamic;
    },
    isFieldHasCustomOptions() {
      return !!this.field.custom_options;
    },
    dynamicField() {
      if (this.isFieldModelWrapper) {
        const key = this.field.model;
        const dynamicField =
          this.$store.getters['wgptAutomationRegistry/getDynamicOptions'](
            key
          ) || {};
        return {
          ...this.field,
          ...dynamicField,
        };
      }
      if (this.isFieldDynamic) {
        const key = this.optionsCacheKey || this.field.model;
        const dynamicField =
          this.$store.getters['wgptAutomationRegistry/getDynamicOptions'](
            key
          ) || {};
        return {
          ...this.field,
          ...dynamicField,
        };
      }
      if (this.isFieldHasCustomOptions) {
        const dynamicField =
          this.$store.getters['wgptAutomationRegistry/getCustomOptions'](
            this.field.custom_options
          ) || {};
        return {
          ...this.field,
          ...dynamicField,
        };
      }
      return this.field;
    },
    isTextType() {
      return (
        this.dynamicField.type === 'text' ||
        this.dynamicField.type === 'textarea'
      );
    },
    isRichtextType() {
      return this.dynamicField.type === 'richtext';
    },
    isNumberType() {
      return this.dynamicField.type === 'number';
    },
    isEmailType() {
      return this.dynamicField.type === 'email';
    },
    isUrlType() {
      return this.dynamicField.type === 'url';
    },
    isDateType() {
      return (
        this.dynamicField.type === 'date' ||
        this.dynamicField.type === 'datetime' ||
        this.dynamicField.type === 'time'
      );
    },
    isSelectType() {
      return this.dynamicField.type === 'select';
    },
    isMultiSelectType() {
      return this.dynamicField.type === 'multiselect';
    },
    isArrayType() {
      return this.dynamicField.type === 'array';
    },
    isGroupInlineType() {
      return (
        this.dynamicField.type === 'group' &&
        this.dynamicField.style === 'inline'
      );
    },
    isGroupType() {
      return this.dynamicField.type === 'group';
    },
    isVariableType() {
      return this.dynamicField.type === 'variable';
    },
    isAttachmentType() {
      return this.dynamicField.type === 'attachment';
    },
    isCheckboxType() {
      return this.dynamicField.type === 'checkbox';
    },
    isBooleanType() {
      return this.dynamicField.type === 'boolean';
    },
    options() {
      if (this.isFieldDynamic) {
        return this.dynamicField.options || [];
      }

      let options = [];
      if (this.isFieldHasCustomOptions) {
        options =
          this.field.custom_options === '_steps'
            ? this.stepOptions
            : (this.dynamicField.options ?? []);
      } else if (this.field.options) {
        options = this.field.options;
      }

      return options.map(option => {
        const label = this.toLocale(
          option.label,
          option.fallbackLabel ?? option.value
        );
        return { ...option, label };
      });
    },
    optionGroups() {
      const optionGroups = this.field.optionGroups || [];
      return optionGroups.map(optionGroup => {
        const label = this.toLocale(
          optionGroup.label,
          optionGroup.fallbackLabel ?? optionGroup.value
        );
        const optionGroupOptions = optionGroup.options || [];
        const options = optionGroupOptions.map(option => {
          const optionLabel = this.toLocale(
            option.label,
            option.fallbackLabel ?? option.value
          );
          return { ...option, label: optionLabel };
        });
        return { label, options };
      });
    },
    stepOptions() {
      return this.parentSteps
        .filter(step => step.action !== 'jump_to_step')
        .map(step => {
          let fallbackLabel;
          if (step.name) {
            fallbackLabel = step.name;
          } else if (step.action === 'branch') {
            fallbackLabel = this.$t(
              'WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_CONDITION_NAME'
            );
          } else if (step.action === 'for_each') {
            fallbackLabel = this.$t(
              'WGPT_AUTOMATIONS.EDITOR.FOR_EACH.FALLBACK_NAME'
            );
          } else {
            fallbackLabel = this.toLocale(
              this.$store.getters['wgptAutomationRegistry/getAction'](
                step.action
              )?.name
            );
          }
          return {
            fallbackLabel,
            value: step.id,
          };
        });
    },
    groupValue() {
      return this.value || this.dynamicField.default || {};
    },
    arrayValue() {
      return this.value || this.dynamicField.default || [];
    },
    valueType() {
      return this.dynamicField.value_type;
    },
    disabledAddArrayControl() {
      return (
        this.dynamicField.max && this.arrayValue.length >= this.dynamicField.max
      );
    },
    disabledRemoveArrayControl() {
      return (
        this.dynamicField.min && this.arrayValue.length <= this.dynamicField.min
      );
    },
    isAlphaNumericPlusUnderscoreOnly() {
      return this.dynamicField.is_variable || this.dynamicField.is_variable_key;
    },
    descriptionTooltip() {
      if (!this.dynamicField.description) return null;

      return {
        content: this.toLocale(this.dynamicField.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
    fixedArrayRecord() {
      if (!this.field.fixed_controls) return undefined;
      if (!this.fixedControlsCacheKey) return undefined;

      return this.$store.getters['wgptAutomationRegistry/getDynamicArrays'](
        this.fixedControlsCacheKey
      );
    },
    fixedArrayItems() {
      const fixedControls = this.field.fixed_controls;
      if (!fixedControls) return [];
      if (!this.fixedArrayRecord?.items?.length) return [];

      return this.fixedArrayRecord.items.map(item => ({
        controls: this.resolveFixedItemControls(fixedControls, item),
      }));
    },
    isLoadingFixedItems() {
      if (!this.field.fixed_controls) return false;
      return !this.fixedArrayRecord;
    },
    groupInlineGridClass() {
      const controls = Object.keys(this.dynamicField.controls).length;
      return `grid grid-cols-${controls} gap-2`;
    },
  },
  watch: {
    field: {
      handler(field, fieldBefore) {
        if (fieldBefore !== undefined) {
          // Only reset validation when the field definition itself changed,
          // not when the parent re-created the wrapper object with the same content.
          const fieldChanged =
            field.name !== fieldBefore.name || field.type !== fieldBefore.type;
          if (fieldChanged) {
            this.$nextTick(() => {
              this.resetValidation();
            });
          }
        }

        if (this.disabled) return;

        const { model, custom_object, custom_options } = field;
        const {
          model: modelBefore,
          custom_object: customObjectBefore,
          custom_options: customOptionsBefore,
        } = fieldBefore || {};

        if (model !== modelBefore || custom_object !== customObjectBefore) {
          this.dispatchWrappers();
        }
        if (custom_options !== customOptionsBefore) {
          this.dispatchCustomOptions();
        }

        const fixedModel = field.fixed_controls?.model;
        const fixedModelBefore = fieldBefore?.fixed_controls?.model;
        if (fixedModel && fixedModel !== fixedModelBefore) {
          this.dispatchFixedControls();
        }

        if (field.controls && !fieldBefore?.controls) {
          this.dispatchDynamicFlags();
        }
      },
      immediate: true,
    },
    params(params, paramsBefore = {}) {
      if (JSON.stringify(params) === JSON.stringify(paramsBefore)) return;
      this.dispatchWrappers();
      this.dispatchFixedControls();
    },
    disabled(disabled, disabledBefore) {
      if (disabled === disabledBefore) return;
      this.dispatchWrappers();
    },
  },
  validations() {
    return {
      arrayValue: {
        duplicate: this.duplicateIf(
          this.isArrayType && !this.field.fixed_controls,
          this.dynamicField
        ),
      },
    };
  },
  mounted() {
    if (
      this.visible &&
      this.value == null &&
      this.dynamicField.default != null
    ) {
      this.$emit('change', this.dynamicField.default);
    }
  },
  methods: {
    hasTemplateVariable(params) {
      return Object.values(params).some(
        v => typeof v === 'string' && v.includes('{{')
      );
    },
    dispatchWrappersCallback() {
      const field = this.field;
      if (field.wrapper) {
        if (field.model) {
          this.$store.dispatch(
            'wgptAutomationRegistry/getDynamicOptions',
            field.model
          );
        } else if (field.custom_object) {
          this.$store.dispatch(
            'wgptAutomationRegistry/getCustomObjects',
            field.custom_object
          );
        }
        return;
      }

      if (field.model) {
        const params = this.params || {};
        const paramFields = field.params ?? [];
        if (
          paramFields.length > 0 &&
          paramFields.some(p => !params[p] && params[p] !== false)
        ) {
          return;
        }
        if (this.hasTemplateVariable(params)) return;

        this.optionsCacheKey = dynamicCacheKey(field.model, params);
        const args = {
          fieldName: field.model,
          params,
        };
        this.$store.dispatch('wgptAutomationRegistry/getDynamicOptions', args);
      }
    },
    dispatchFixedControls() {
      const fixedControls = this.field.fixed_controls;
      if (!fixedControls) return;

      const params = this.params || {};
      const paramFields = fixedControls.params ?? [];
      if (
        paramFields.length > 0 &&
        paramFields.some(p => !params[p] && params[p] !== false)
      ) {
        return;
      }
      if (this.hasTemplateVariable(params)) return;

      this.fixedControlsCacheKey = dynamicCacheKey(fixedControls.model, params);
      this.$store.dispatch('wgptAutomationRegistry/getDynamicArrays', {
        fieldName: fixedControls.model,
        params,
      });
    },
    isLoadingVisibility(field) {
      const conditions = field.show_if ?? [];
      if (!conditions.length) return false;
      const attributes = this.value ?? {};
      return conditions.some(condition => {
        if (!condition.dynamic_flag) return false;
        const paramFields = condition.params ?? [];
        const params = {};
        paramFields.forEach(p => {
          params[p] = attributes[p];
        });
        if (paramFields.some(p => !params[p] && params[p] !== false)) {
          return false;
        }
        const cacheKey = dynamicCacheKey(condition.dynamic_flag, params);
        return !this.$store.getters['wgptAutomationRegistry/getDynamicFlags'](
          cacheKey
        );
      });
    },
    isDynamicFlagParam(fieldName) {
      const controls = this.field.controls;
      if (!controls) return false;
      return controls.some(control =>
        (control.show_if ?? []).some(
          c => c.dynamic_flag && (c.params ?? []).includes(fieldName)
        )
      );
    },
    dispatchDynamicFlags() {
      const controls = this.field.controls;
      if (!controls) return;

      const value = this.value ?? {};
      const seenKeys = new Set();

      controls.forEach(control => {
        const conditions = control.show_if ?? [];
        conditions.forEach(condition => {
          if (!condition.dynamic_flag) return;

          const flagName = condition.dynamic_flag;
          const paramFields = condition.params ?? [];
          const params = {};
          paramFields.forEach(p => {
            params[p] = value[p];
          });

          if (paramFields.some(p => !params[p] && params[p] !== false)) return;
          if (this.hasTemplateVariable(params)) return;

          const cacheKey = dynamicCacheKey(flagName, params);
          if (seenKeys.has(cacheKey)) return;
          seenKeys.add(cacheKey);

          this.$store.dispatch('wgptAutomationRegistry/getDynamicFlags', {
            flagName,
            params,
          });
        });
      });
    },
    resolveFixedItemControls(fixedControls, item) {
      const resolvedControls = (fixedControls.controls || []).map(control => {
        const resolved = {};
        Object.entries(control).forEach(([key, val]) => {
          if (typeof val === 'string' && val.includes('%{')) {
            resolved[key] = val.replace(
              /%\{([^}]+)\}/g,
              (_, attr) => item[attr] ?? ''
            );
          } else {
            resolved[key] = val;
          }
        });
        return resolved;
      });

      if (fixedControls.style) {
        return [
          {
            type: 'group',
            style: fixedControls.style,
            controls: resolvedControls,
          },
        ];
      }
      return resolvedControls;
    },
    dispatchCustomOptions() {
      const { custom_options } = this.field;
      if (!custom_options) return;
      if (['_steps'].includes(custom_options)) return;

      this.$store.dispatch(
        'wgptAutomationRegistry/getCustomOptions',
        custom_options
      );
    },
    resetValidation() {
      this.v$.$reset();
    },
    isDisabled(field) {
      if (this.disabled) return true;
      if (field.disabled) return true;
      const results = this.resolveConditions(field.disabled_unless);
      return !results.every(result => result);
    },
    isVisible(field) {
      const results = this.resolveConditions(field.show_if);
      return results.every(result => result);
    },
    resolveConditions(conditions = []) {
      const attributes = this.value ?? {};
      return conditions.map(condition => {
        if (condition.dynamic_flag) {
          return this.resolveDynamicFlagCondition(condition, attributes);
        }
        const { field: fieldName, value } = condition;
        const currentValue =
          attributes[fieldName] ?? this.getDefaultFieldValue(fieldName);
        if (currentValue == null) return false;
        const values = Array.isArray(value) ? value : [value];
        return values.some(v => {
          if (v.includes('*')) {
            if (v === '*') return currentValue;
            return `${currentValue}`.includes(v.replace(/\*/g, ''));
          }
          return currentValue === v;
        });
      });
    },
    resolveDynamicFlagCondition(condition, attributes) {
      const flagName = condition.dynamic_flag;
      const paramFields = condition.params ?? [];
      const params = {};
      paramFields.forEach(p => {
        params[p] = attributes[p];
      });
      if (paramFields.some(p => !params[p] && params[p] !== false)) {
        return false;
      }
      const cacheKey = dynamicCacheKey(flagName, params);
      const record =
        this.$store.getters['wgptAutomationRegistry/getDynamicFlags'](cacheKey);
      if (!record) return false;
      return !!record.value;
    },
    getDefaultFieldValue(fieldName) {
      const field = this.field.controls.find(({ name }) => name === fieldName);
      return field?.default;
    },
    groupControlChange(value, controlName) {
      const groupValue = {
        ...this.groupValue,
        [controlName]: value,
      };
      const controls = this.dynamicField.controls || [];
      controls.forEach(control => {
        if (control.name === controlName) return;
        // Clear hidden fields
        if (
          control.show_if?.length &&
          !this.isVisibleWith(control, groupValue)
        ) {
          delete groupValue[control.name];
          return;
        }
        // Clear param-dependent select fields whose params changed
        // (fixed array fields handle their own cleanup via fixedItems watcher)
        const paramDeps = control.params ?? [];
        if (paramDeps.includes(controlName)) {
          delete groupValue[control.name];
        }
      });
      this.$emit('change', groupValue);
      if (this.isDynamicFlagParam(controlName)) {
        this.$nextTick(() => this.dispatchDynamicFlags());
      }
    },
    isVisibleWith(field, attributes) {
      const conditions = field.show_if ?? [];
      if (!conditions.length) return true;
      return conditions.every(condition => {
        if (condition.dynamic_flag) {
          return this.resolveDynamicFlagCondition(condition, attributes);
        }
        const { field: fieldName, value } = condition;
        const actual = attributes[fieldName];
        if (actual == null) return false;
        const values = Array.isArray(value) ? value : [value];
        return values.some(v => {
          if (v.includes('*')) {
            if (v === '*') return !!actual;
            return `${actual}`.includes(v.replace(/\*/g, ''));
          }
          return actual === v;
        });
      });
    },
    onChange(value) {
      this.$emit('change', value, this.dynamicField);
    },
    constructParams(field) {
      const params = {};
      const paramFields = [
        ...(field.params ?? []),
        ...(field.fixed_controls?.params ?? []),
      ];
      const value = this.value ?? {};
      paramFields.forEach(paramField => {
        params[paramField] = value[paramField] ?? this.params?.[paramField];
      });
      return params;
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="visible">
    <TextField
      v-if="isTextType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :type="dynamicField.type"
      :required="dynamicField.required"
      :disabled="disabled"
      :variables="variables"
      :show-variables-selector="showVariablesSelector"
      :alphanumeric-plus-underscore-only="isAlphaNumericPlusUnderscoreOnly"
      :dirty="dirty"
      @change="onChange"
    />
    <NumberField
      v-else-if="isNumberType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :min="dynamicField.min"
      :max="dynamicField.max"
      :disabled="disabled"
      :variables="variables"
      :show-variables-selector="showVariablesSelector"
      :dirty="dirty"
      @change="onChange"
    />
    <EmailField
      v-else-if="isEmailType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :disabled="disabled"
      :variables="variables"
      :show-variables-selector="showVariablesSelector"
      :dirty="dirty"
      @change="onChange"
    />
    <UrlField
      v-else-if="isUrlType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :disabled="disabled"
      :variables="variables"
      :show-variables-selector="showVariablesSelector"
      :dirty="dirty"
      @change="onChange"
    />
    <RichtextField
      v-else-if="isRichtextType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :variables="variables"
      :show-variables-selector="showVariablesSelector"
      :dirty="dirty"
      @change="onChange"
    />
    <DateField
      v-else-if="isDateType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :variables="variables"
      :type="dynamicField.type"
      :disabled="disabled"
      :dirty="dirty"
      @change="onChange"
    />
    <SelectField
      v-else-if="isSelectType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :options="options"
      :option-groups="optionGroups"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :value-type="valueType"
      :disabled="disabled"
      :dirty="dirty"
      @change="onChange"
    />
    <MultiSelectField
      v-else-if="isMultiSelectType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :options="options"
      :values="arrayValue"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :disabled="disabled"
      :dirty="dirty"
      @change="onChange"
    />
    <VariableField
      v-else-if="isVariableType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :value-type="valueType"
      :disabled="disabled"
      :variables="variables"
      :dirty="dirty"
      @change="onChange"
    />
    <CheckboxField
      v-else-if="isCheckboxType"
      :label="dynamicField.label"
      :option-label="dynamicField.option_label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :disabled="disabled"
      :dirty="dirty"
      @change="onChange"
    />
    <BooleanField
      v-else-if="isBooleanType"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :placeholder="dynamicField.placeholder"
      :value="value"
      :default-value="dynamicField.default"
      :required="dynamicField.required"
      :disabled="disabled"
      :dirty="dirty"
      @change="onChange"
    />

    <div v-else-if="isGroupInlineType">
      <div :class="groupInlineGridClass">
        <template v-for="control in dynamicField.controls" :key="control.name">
          <div
            v-if="isLoadingVisibility(control)"
            class="flex-1 shrink-0 space-y-2 animate-loader-pulse"
          >
            <div class="h-3 bg-n-alpha-3 dark:bg-n-alpha-2 rounded w-1/4" />
            <div class="h-10 bg-n-alpha-3 dark:bg-n-alpha-2 rounded" />
          </div>
          <dynamic-field
            v-else
            class="flex-1 shrink-0"
            :field="control"
            :value="groupValue[control.name]"
            :visible="isVisible(control)"
            :disabled="isDisabled(control)"
            :variables="variables"
            :show-variables-selector="showVariablesSelector"
            :alphanumeric-plus-underscore-only="
              isAlphaNumericPlusUnderscoreOnly
            "
            :params="constructParams(control)"
            :dirty="dirty"
            @change="value => groupControlChange(value, control.name)"
          />
        </template>
      </div>
    </div>

    <div v-else-if="isGroupType" class="flex flex-col gap-6">
      <label
        v-if="dynamicField.label || dynamicField.description"
        class="flex items-center gap-2"
      >
        <span v-if="dynamicField.label" class="block truncate">{{
          toLocale(dynamicField.label, dynamicField.fallbackLabel)
        }}</span>
        <IconNext
          v-if="descriptionTooltip"
          v-tooltip.top="descriptionTooltip"
          icon="i-lucide-info"
          class="text-n-slate-11 size-3.5"
        />
      </label>
      <template v-for="control in dynamicField.controls" :key="control.name">
        <div
          v-if="isLoadingVisibility(control)"
          class="space-y-2 animate-loader-pulse"
        >
          <div class="h-3 bg-n-alpha-3 dark:bg-n-alpha-2 rounded w-1/4" />
          <div class="h-10 bg-n-alpha-3 dark:bg-n-alpha-2 rounded" />
        </div>
        <dynamic-field
          v-else
          :field="control"
          :value="groupValue[control.name]"
          :visible="isVisible(control)"
          :disabled="isDisabled(control)"
          :variables="variables"
          :show-variables-selector="showVariablesSelector"
          :alphanumeric-plus-underscore-only="isAlphaNumericPlusUnderscoreOnly"
          :params="constructParams(control)"
          :dirty="dirty"
          @change="value => groupControlChange(value, control.name)"
        />
      </template>
    </div>

    <ArrayField
      v-else-if="isArrayType"
      v-slot="slotProps"
      :field="dynamicField"
      :values="arrayValue"
      :fixed-items="fixedArrayItems"
      :loading-fixed-items="isLoadingFixedItems"
      :disabled="disabled"
      :dirty="dirty"
      @change="onChange"
    >
      <dynamic-field
        v-for="control in slotProps.controls || dynamicField.controls"
        :key="control.name"
        :field="control"
        :disabled="disabled"
        :variables="variables"
        :show-variables-selector="showVariablesSelector"
        :value="slotProps.value"
        :params="params"
        :alphanumeric-plus-underscore-only="isAlphaNumericPlusUnderscoreOnly"
        :dirty="dirty"
        class="flex-1"
        @change="slotProps.change"
      />
    </ArrayField>

    <AttachmentField
      v-else-if="isAttachmentType"
      :field="dynamicField"
      :label="dynamicField.label"
      :description="dynamicField.description"
      :fallback-label="dynamicField.fallbackLabel"
      :value="value"
      :required="dynamicField.required"
      :disabled="disabled"
      :variables="variables"
      :dirty="dirty"
      @change="onChange"
    />
  </div>
</template>
